import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  MapPin,
  Clock,
  Weight,
  Package,
  DollarSign,
  Truck,
  User,
  Hash,
  HashIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Load } from "@/api/vendor/getLoads";
import LoadEventCard from "@/broker/LoadEventCard";
import { Separator } from "@/components/ui/separator";

type Props = {
  load: Load;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  badgeColor?: string;
};

export const ViewLoadModal = ({
  load,
  setIsOpen,
  isOpen,
  badgeColor = "bg-blue-100 text-blue-800",
}: Props) => {
  const handleClose = () => {
    setIsOpen(false);
  };

  const formatDate = (dateTime: Date) => {
    return new Date(dateTime).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const formatTime = (dateTime: Date) => {
    return new Date(dateTime).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatWeight = (weight: number) => {
    return `${(weight / 1000).toFixed(1)}k lbs`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] rounded-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            Load {load.name} -{" "}
            <Badge variant="warning" className={badgeColor}>
              {load.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid sm:grid-cols-1 grid-cols-2 gap-6">
          {/* Load Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Load Information</h3>
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Route</p>
                    <p className="text-sm text-muted-foreground">
                      {load.sourceLocation} → {load.destinationLocation}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Schedule</p>
                    <p className="text-sm text-muted-foreground">
                      Pickup Window:{" "}
                      {load.pickupStart && load.pickupEnd
                        ? formatDate(load.pickupStart) +
                          " - " +
                          formatTime(load.pickupEnd)
                        : "No pickup date set"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Delivery Window:{" "}
                      {load.deliveryStart && load.deliveryEnd
                        ? formatDate(load.deliveryStart) +
                          " - " +
                          formatTime(load.deliveryEnd)
                        : "No delivery date set"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <Weight className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Weight</p>
                      <p className="text-sm text-muted-foreground">
                        {load.weight
                          ? formatWeight(load.weight)
                          : "No weight set"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Pieces</p>
                      <p className="text-sm text-muted-foreground">
                        {load.pieces}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <HashIcon className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Bol</p>
                      <p className="text-sm text-muted-foreground">
                        {load.bol !== "" ? load.bol : "No BOL Supplied Yet"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Cost</p>
                      <p className="text-sm text-muted-foreground">
                        {load.receiverCost
                          ? formatCurrency(load.receiverCost)
                          : "No cost set"}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <LoadEventCard events={load.events} />
          </div>

          {/* Assignment Form */}
          <div className="space-y-4 flex flex-col">
            <h3 className="text-lg font-semibold">Carrier Details</h3>

            <Card className="grow">
              <CardContent className="p-4 space-y-4">
                {/* Selected Carrier Info */}
                {load.status !== "Not Assigned" ? (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src="/placeholder.svg" />
                        <AvatarFallback>
                          {load!
                            .carrierName!.split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-gray-900">
                          {load.carrierName}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium text-gray-900">
                          No Carrier Assigned
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Driver Information */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <h4 className="font-medium">Driver Information</h4>
                  </div>
                  {load.status !== "Not Assigned" &&
                  load.driverName &&
                  load.driverLicenseNumber ? (
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label htmlFor="driverName">Driver Name</Label>
                        <p className="text-sm text-muted-foreground">
                          {load.driverName ?? "TBD"}
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="driverLicenseNumber">
                          Driver License Number
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {load.driverLicenseNumber}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Driver information not available.
                    </div>
                  )}
                </div>

                {/* Truck Information */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="w-4 h-4 text-muted-foreground" />
                    <h4 className="font-medium">Truck Information</h4>
                  </div>
                  {load.status !== "Not Assigned" &&
                  load.truckVinLast6 &&
                  load.truckLicensePlate ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="truckVinLast6">
                          Truck VIN (Last 6)
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {load.truckVinLast6}
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="truckLicensePlate">
                          Truck License Plate
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {load.truckLicensePlate}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Truck information not available.
                    </div>
                  )}
                </div>

                {/* Trailer Information */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center gap-2 mb-2">
                    <Hash className="w-4 h-4 text-muted-foreground" />
                    <h4 className="font-medium">Trailer Information</h4>
                  </div>
                  {load.trailerNumber && load.trailerLicensePlate ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="trailerNumber">Trailer Number</Label>
                        <p className="text-sm text-muted-foreground">
                          {load.trailerNumber}
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="trailerLicensePlate">
                          Trailer License Plate
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {load.trailerLicensePlate}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Trailer information not available.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Separator className="my-2" />
        <DialogFooter className="flex justify-center items-center">
          <div className="flex gap-2">
            <Button
              className="w-28"
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
